import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button, MultiSelectModal } from '@/components/ui';
import { fetchCouponOptions, resolveCouponOption } from './couponSelectLoaders';

export interface CouponLinkDraft {
  couponId: string;
  /** Display label resolved at selection time (raw couponId 미노출). */
  label: string;
}

export interface ProductCouponsFieldProps {
  value: ReadonlyArray<CouponLinkDraft>;
  disabled?: boolean;
  onChange: (links: CouponLinkDraft[]) => void;
}

export const ProductCouponsField = ({ value, disabled, onChange }: ProductCouponsFieldProps) => {
  const [open, setOpen] = useState(false);

  const selectedIds = value.map((link) => link.couponId);
  const labelById = new Map<string, string>(value.map((link) => [link.couponId, link.label]));

  const handleApply = (nextIds: ReadonlyArray<string>, resolved: Map<string, string>) => {
    const next = nextIds.map<CouponLinkDraft>((couponId) => {
      const existing = value.find((link) => link.couponId === couponId);
      if (existing) return existing;
      return {
        couponId,
        label: resolved.get(couponId) ?? labelById.get(couponId) ?? couponId,
      };
    });
    onChange(next);
  };

  const removeLink = (couponId: string) => {
    onChange(value.filter((link) => link.couponId !== couponId));
  };

  return (
    <div className="flex flex-col gap-3" data-testid="product-coupons-field">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
          이 상품에 적용할 쿠폰을 선택하세요. 저장 시 적용 쿠폰이 갱신됩니다. (전역 쿠폰 제외)
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setOpen(true)}
          data-testid="product-coupons-field-trigger"
        >
          {value.length > 0 ? '쿠폰 변경' : '쿠폰 선택'}
        </Button>
      </div>

      {value.length === 0 ? (
        <div
          className="rounded-md py-6 text-center text-[13px]"
          style={{
            background: 'var(--surface-0)',
            border: '1px dashed var(--divider-strong)',
            color: 'var(--content-muted)',
          }}
        >
          이 상품에 직접 매핑된 쿠폰이 없습니다.
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {value.map((link) => (
            <li
              key={link.couponId}
              className="flex items-center justify-between gap-3 rounded-md p-3"
              style={{
                background: 'var(--surface-1)',
                border: '1px solid var(--divider-strong)',
              }}
            >
              <div className="flex min-w-0 flex-col gap-1">
                <span
                  className="text-[11px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--content-muted)' }}
                >
                  쿠폰
                </span>
                <span
                  className="truncate text-[14px] font-semibold"
                  style={{ color: 'var(--content-strong)' }}
                  title={link.label}
                >
                  {link.label}
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => removeLink(link.couponId)}
                aria-label="쿠폰 연결 해제"
              >
                <Trash2 size={14} style={{ color: 'var(--signal-danger)' }} />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <CouponSelectModal
        open={open}
        onOpenChange={setOpen}
        initialValue={selectedIds}
        onApply={handleApply}
      />
    </div>
  );
};

interface CouponSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: ReadonlyArray<string>;
  onApply: (ids: ReadonlyArray<string>, resolved: Map<string, string>) => void;
}

const CouponSelectModal = ({
  open,
  onOpenChange,
  initialValue,
  onApply,
}: CouponSelectModalProps) => {
  const resolvedLabels = new Map<string, string>();

  const resolveOption = async (couponId: string) => {
    const option = await resolveCouponOption(couponId);
    if (option) resolvedLabels.set(couponId, option.label);
    return option;
  };

  return (
    <MultiSelectModal
      open={open}
      onOpenChange={onOpenChange}
      loadOptions={async (keyword, page) => {
        const result = await fetchCouponOptions(keyword, page);
        result.items.forEach((item) => resolvedLabels.set(item.value, item.label));
        return result;
      }}
      resolveOption={resolveOption}
      initialValue={initialValue}
      onApply={(ids) => onApply(ids, resolvedLabels)}
      title="쿠폰 선택"
      description="이 상품에 적용할 쿠폰을 다중 선택하세요."
      searchPlaceholder="쿠폰명으로 검색"
      applyLabel="선택 적용"
      emptySelectionHint="미선택 시 이 상품의 쿠폰 매핑이 모두 해제됩니다."
    />
  );
};
